import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Save,
  LogOut,
  ArrowLeft,
  RefreshCw,
  Trash2,
  Mail,
  Inbox,
  FileEdit,
  User,
  Building2,
  Smartphone,
  Upload,
  X as XIcon,
  ImageIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { API } from "@/context/AuthContext";
import { ZafaraFullLogo } from "@/components/landing/Logo";

const FIELDS = {
  hero: [
    { k: "badge", l: "Badge (petit texte en haut du Hero)", type: "input" },
    { k: "line1", l: "Ligne 1 (avant le mot mis en évidence)", type: "input" },
    { k: "highlight", l: "Mot en évidence (surligné jaune)", type: "input" },
    { k: "line2", l: "Ligne 2", type: "input" },
    { k: "line3", l: "Ligne 3 (dernière, verte)", type: "input" },
    { k: "paragraph", l: "Paragraphe descriptif", type: "textarea", rows: 4 },
  ],
  founder: [
    { k: "name", l: "Nom complet", type: "input" },
    { k: "role", l: "Rôle", type: "input" },
    { k: "photo", l: "Photo (portrait)", type: "photo" },
    { k: "bio", l: "Biographie", type: "textarea", rows: 6 },
    { k: "quote", l: "Citation", type: "input" },
    { k: "email", l: "Email", type: "input" },
    { k: "linkedin", l: "LinkedIn (URL)", type: "input" },
    { k: "twitter", l: "Twitter / X (URL)", type: "input" },
  ],
  company: [
    { k: "form", l: "Forme juridique", type: "input" },
    { k: "siege", l: "Siège social", type: "input" },
    { k: "rccm", l: "N° RCCM", type: "input" },
    { k: "ifu", l: "IFU", type: "input" },
    { k: "email", l: "Email de contact", type: "input" },
    { k: "whatsapp", l: "WhatsApp (format +229…)", type: "input" },
    { k: "hebergeur", l: "Hébergeur (mentions légales)", type: "input" },
  ],
  stores: [
    { k: "appStore", l: "URL App Store", type: "input" },
    { k: "playStore", l: "URL Google Play", type: "input" },
  ],
};

const TABS = [
  { id: "hero", label: "Hero (accueil)", icon: FileEdit },
  { id: "founder", label: "Fondateur", icon: User },
  { id: "company", label: "Société", icon: Building2 },
  { id: "stores", label: "Stores", icon: Smartphone },
  { id: "whyBlocks", label: "Blocs « Pourquoi »", icon: FileEdit },
  { id: "appFeatures", label: "Fonctionnalités app", icon: FileEdit },
  { id: "messages", label: "Messages", icon: Inbox },
];

const LIST_SCHEMAS = {
  whyBlocks: {
    label: "Blocs Pourquoi Zafara (ZA / FAR / A)",
    fields: [
      { k: "letter", l: "Lettre (ex: ZA)", type: "input" },
      { k: "title", l: "Titre", type: "input" },
      { k: "text", l: "Texte descriptif", type: "textarea", rows: 3 },
    ],
    empty: { letter: "", title: "", text: "" },
  },
  appFeatures: {
    label: "Fonctionnalités affichées sur /application",
    fields: [
      { k: "icon", l: "Icône (Store, Truck, Zap, ShieldCheck, MapPin, Smartphone)", type: "input" },
      { k: "title", l: "Titre", type: "input" },
      { k: "text", l: "Texte descriptif", type: "textarea", rows: 3 },
    ],
    empty: { icon: "Store", title: "", text: "" },
  },
};

export default function AdminDashboard() {
  const { user, ready, logout, authHeaders, token } = useAuth();
  const { content: publicContent, refresh: refreshPublic } = useContent();

  const [tab, setTab] = useState("founder");
  const [form, setForm] = useState(publicContent);
  const [saving, setSaving] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  useEffect(() => {
    setForm(publicContent);
  }, [publicContent]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/content`, {
          headers: authHeaders(),
        });
        setForm(data);
      } catch (e) {
        // ignore
      }
    })();
  }, [token, authHeaders]);

  const loadMessages = async () => {
    if (!token) return;
    setLoadingMsgs(true);
    try {
      const { data } = await axios.get(`${API}/admin/messages`, {
        headers: authHeaders(),
      });
      setMessages(data.messages || []);
    } catch {
      toast.error("Impossible de charger les messages.");
    } finally {
      setLoadingMsgs(false);
    }
  };

  useEffect(() => {
    if (tab === "messages") loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  if (!ready) return <div className="min-h-screen bg-[#FBFAF6]" />;
  if (!user) return <Navigate to="/admin/login" replace />;

  const update = (section, key) => (e) =>
    setForm((f) => ({ ...f, [section]: { ...f[section], [key]: e.target.value } }));

  const setField = (section, key, value) =>
    setForm((f) => ({ ...f, [section]: { ...f[section], [key]: value } }));

  const uploadPhoto = async (file, section, key) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error("Formats acceptés : JPG, PNG, WEBP.");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Image trop lourde (max 3 Mo).");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      setField(section, key, data.url);
      toast.success("Photo téléversée. N'oubliez pas d'enregistrer.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Échec du téléversement.";
      toast.error(typeof msg === "string" ? msg : "Échec du téléversement.");
    }
  };

  const buildImageUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http") || url.startsWith("data:")) return url;
    return `${process.env.REACT_APP_BACKEND_URL || ""}${url}`;
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content`, form, { headers: authHeaders() });
      await refreshPublic();
      toast.success("Modifications enregistrées.");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    try {
      await axios.delete(`${API}/admin/messages/${id}`, { headers: authHeaders() });
      setMessages((m) => m.filter((x) => x.id !== id));
      toast.success("Message supprimé.");
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  const setListField = (section, index, key, value) =>
    setForm((f) => ({
      ...f,
      [section]: f[section].map((item, i) => (i === index ? { ...item, [key]: value } : item)),
    }));

  const addListItem = (section) =>
    setForm((f) => ({ ...f, [section]: [...(f[section] || []), { ...LIST_SCHEMAS[section].empty }] }));

  const removeListItem = (section, index) =>
    setForm((f) => ({ ...f, [section]: f[section].filter((_, i) => i !== index) }));

  const renderList = (section) => {
    const schema = LIST_SCHEMAS[section];
    const items = form[section] || [];
    return (
      <div className="space-y-4">
        <p className="text-sm text-[#16241B]/60">{schema.label}</p>
        {items.map((item, i) => (
          <div key={i} className="rounded-2xl border border-[#EDE7D9] bg-[#FBFAF6] p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="font-utility text-[10px] uppercase tracking-widest text-[#0E7A3B]">Bloc {i + 1}</p>
              <button
                type="button"
                onClick={() => removeListItem(section, i)}
                className="text-xs text-[#E8112D] hover:underline inline-flex items-center gap-1"
              >
                <XIcon className="h-3 w-3" /> Supprimer
              </button>
            </div>
            <div className="space-y-3">
              {schema.fields.map((f) => (
                <label key={f.k} className="block">
                  <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">
                    {f.l}
                  </span>
                  {f.type === "textarea" ? (
                    <textarea
                      value={item[f.k] || ""}
                      onChange={(e) => setListField(section, i, f.k, e.target.value)}
                      rows={f.rows || 3}
                      className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#0E7A3B] focus:border-[#0E7A3B] transition-colors resize-y"
                    />
                  ) : (
                    <input
                      type="text"
                      value={item[f.k] || ""}
                      onChange={(e) => setListField(section, i, f.k, e.target.value)}
                      className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#0E7A3B] focus:border-[#0E7A3B] transition-colors"
                    />
                  )}
                </label>
              ))}
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addListItem(section)}
          className="w-full rounded-2xl border-2 border-dashed border-[#EDE7D9] bg-white py-3 text-sm font-medium text-[#0E7A3B] hover:border-[#0E7A3B] transition-colors"
        >
          + Ajouter un bloc
        </button>
      </div>
    );
  };

  const renderSection = (section) => {
    if (section === "whyBlocks" || section === "appFeatures") return null;
    return (
    <div className="space-y-5">
      {FIELDS[section].map((f) => (
        <label key={f.k} className="block">
          <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">
            {f.l}
          </span>
          {f.type === "textarea" ? (
            <textarea
              data-testid={`admin-${section}-${f.k}`}
              value={form[section]?.[f.k] || ""}
              onChange={update(section, f.k)}
              rows={f.rows || 4}
              className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] px-4 py-3 text-base text-[#16241B] focus:outline-none focus:ring-2 focus:ring-[#0E7A3B] focus:border-[#0E7A3B] transition-colors resize-y"
            />
          ) : f.type === "photo" ? (
            <div className="mt-2 flex flex-col sm:flex-row gap-4">
              <div className="h-32 w-32 shrink-0 rounded-2xl border border-[#EDE7D9] bg-[#FBFAF6] overflow-hidden flex items-center justify-center">
                {form[section]?.[f.k] ? (
                  <img
                    src={buildImageUrl(form[section][f.k])}
                    alt="Aperçu"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <ImageIcon className="h-8 w-8 text-[#16241B]/30" />
                )}
              </div>
              <div className="flex-1 flex flex-col gap-2">
                <label
                  data-testid={`admin-${section}-${f.k}-upload`}
                  className="inline-flex items-center gap-2 rounded-full bg-[#0E7A3B] px-5 py-2.5 text-sm font-semibold text-white cursor-pointer hover:bg-[#F5A800] hover:text-[#16241B] transition-colors w-fit"
                >
                  <Upload className="h-4 w-4" />
                  Choisir une image
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => uploadPhoto(e.target.files?.[0], section, f.k)}
                  />
                </label>
                <p className="text-xs text-[#16241B]/50">
                  JPG, PNG ou WEBP · 3 Mo max · ratio portrait recommandé (4:5).
                </p>
                {form[section]?.[f.k] && (
                  <button
                    type="button"
                    onClick={() => setField(section, f.k, "")}
                    className="inline-flex items-center gap-1.5 text-xs text-[#E8112D] hover:underline w-fit"
                  >
                    <XIcon className="h-3 w-3" /> Retirer la photo
                  </button>
                )}
              </div>
            </div>
          ) : (
            <input
              data-testid={`admin-${section}-${f.k}`}
              type="text"
              value={form[section]?.[f.k] || ""}
              onChange={update(section, f.k)}
              className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] px-4 py-3 text-base text-[#16241B] focus:outline-none focus:ring-2 focus:ring-[#0E7A3B] focus:border-[#0E7A3B] transition-colors"
            />
          )}
        </label>
      ))}
    </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FBFAF6]">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FBFAF6]/90 border-b border-[#EDE7D9]">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="hover:-translate-y-0.5 transition-transform">
              <ZafaraFullLogo />
            </Link>
            <span className="hidden md:inline-flex items-center rounded-full bg-[#0E7A3B]/10 text-[#0E7A3B] px-3 py-1 text-xs font-utility uppercase tracking-widest">
              <FileEdit className="h-3 w-3 mr-1.5" /> Administration
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden md:inline-flex items-center gap-2 rounded-full border border-[#EDE7D9] bg-white px-4 py-2 text-sm hover:border-[#0E7A3B] hover:text-[#0E7A3B] transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Voir le site
            </Link>
            <button
              data-testid="admin-logout"
              onClick={() => {
                logout();
                toast.success("Vous êtes déconnecté.");
              }}
              className="inline-flex items-center gap-2 rounded-full bg-[#16241B] text-white px-4 py-2 text-sm font-medium hover:bg-[#E8112D] transition-colors"
            >
              <LogOut className="h-4 w-4" /> Se déconnecter
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 sm:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-3">
            <p className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/50">
              Connecté en tant que
            </p>
            <p className="mt-1 font-display text-base text-[#16241B]">
              {user.email}
            </p>

            <nav className="mt-8 space-y-1">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  data-testid={`admin-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`w-full flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
                    tab === t.id
                      ? "bg-[#0E7A3B] text-white shadow-[0_6px_20px_rgba(14,122,59,0.25)]"
                      : "text-[#16241B]/75 hover:bg-white"
                  }`}
                >
                  <t.icon className="h-4 w-4" />
                  {t.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <section className="lg:col-span-9">
            {tab === "messages" ? (
              <div className="rounded-3xl border border-[#EDE7D9] bg-white p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-display text-2xl text-[#16241B] tracking-tight">
                      Boîte de réception
                    </h2>
                    <p className="mt-1 text-sm text-[#16241B]/60">
                      {messages.length} message{messages.length > 1 ? "s" : ""}
                    </p>
                  </div>
                  <button
                    onClick={loadMessages}
                    className="inline-flex items-center gap-2 rounded-full border border-[#EDE7D9] bg-white px-4 py-2 text-sm hover:border-[#0E7A3B] hover:text-[#0E7A3B] transition-colors"
                  >
                    <RefreshCw className={`h-4 w-4 ${loadingMsgs ? "animate-spin" : ""}`} />
                    Rafraîchir
                  </button>
                </div>

                {messages.length === 0 ? (
                  <div className="rounded-2xl bg-[#FBFAF6] border border-dashed border-[#EDE7D9] p-12 text-center">
                    <Inbox className="h-8 w-8 mx-auto text-[#16241B]/30" />
                    <p className="mt-4 text-sm text-[#16241B]/60">
                      Aucun message pour le moment.
                    </p>
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {messages.map((m) => (
                      <li
                        key={m.id}
                        data-testid={`admin-message-${m.id}`}
                        className="rounded-2xl border border-[#EDE7D9] bg-[#FBFAF6] p-5"
                      >
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div>
                            <p className="font-display font-semibold text-base text-[#16241B]">
                              {m.name}
                            </p>
                            <a
                              href={`mailto:${m.email}`}
                              className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-[#0E7A3B] hover:underline"
                            >
                              <Mail className="h-3 w-3" /> {m.email}
                            </a>
                          </div>
                          <p className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/50">
                            {new Date(m.created_at).toLocaleString("fr-FR")}
                          </p>
                        </div>
                        <p className="mt-3 font-display text-sm text-[#16241B]/80">
                          {m.subject}
                        </p>
                        <p className="mt-2 text-sm text-[#16241B]/75 whitespace-pre-wrap">
                          {m.message}
                        </p>
                        <div className="mt-4 flex items-center justify-between">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-utility uppercase tracking-widest ${
                              m.email_sent
                                ? "bg-[#0E7A3B]/10 text-[#0E7A3B]"
                                : "bg-[#F5A800]/15 text-[#F5A800]"
                            }`}
                          >
                            {m.email_sent ? "Email envoyé" : "Email non envoyé"}
                          </span>
                          <button
                            onClick={() => deleteMessage(m.id)}
                            className="inline-flex items-center gap-1.5 text-xs text-[#E8112D] hover:underline"
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Supprimer
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className="rounded-3xl border border-[#EDE7D9] bg-white p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-display text-2xl text-[#16241B] tracking-tight capitalize">
                      {TABS.find((t) => t.id === tab)?.label}
                    </h2>
                    <p className="mt-1 text-sm text-[#16241B]/60">
                      Modifications appliquées en direct sur le site public.
                    </p>
                  </div>
                  <button
                    data-testid="admin-save"
                    onClick={save}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-full bg-[#0E7A3B] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(14,122,59,0.3)] hover:bg-[#F5A800] hover:text-[#16241B] disabled:opacity-60 transition-all duration-300"
                  >
                    <Save className="h-4 w-4" />
                    {saving ? "Enregistrement…" : "Enregistrer"}
                  </button>
                </div>

                {(tab === "whyBlocks" || tab === "appFeatures") ? renderList(tab) : renderSection(tab)}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
